import React, { useState, ReactNode } from 'react';
import './Tabs.scss';

/**
 * @description Props for the Tab component
 * @interface TabProps
 */
interface TabProps {
  /** Whether the tab is currently active */
  isActive?: boolean;
  /** Content to be displayed inside the tab */
  children: ReactNode;
  /** Title of the tab displayed in the tab button */
  title: string;
  /** Additional CSS classes to apply to the tab */
  classes?: string;
}

/**
 * @description Props for the Tabs component
 * @interface TabsProps
 */
interface TabsProps {
  /** Array of Tab components to be rendered */
  children: React.ReactElement<TabProps>[];
}

/**
 * @description Tab component that displays content when active.
 * Content is shown or hidden based on the isActive prop.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {TabProps} props - The component props
 *
 * @returns {JSX.Element} Rendered Tab component
 */
export const Tab: React.FC<TabProps> = ({
  isActive,
  children,
  classes = '',
}) => (
  <div className={classes} style={{ display: isActive ? 'block' : 'none' }}>
    {children}
  </div>
);

/**
 * @description Tabs component that manages a collection of Tab components.
 * Provides tab navigation buttons and controls which tab is currently active.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {TabsProps} props - The component props
 *
 * @returns {JSX.Element} Rendered Tabs component with tab navigation
 */
export const Tabs: React.FC<TabsProps> = ({ children }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      <div className="tabs__buttons">
        {React.Children.map(children, (child, index) => (
          <span
            key={index}
            className={`tabs__link ${activeTab === index ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
            data-testid={`tab-${child.props.title}-${index}`}
          >
            {child.props.title}
          </span>
        ))}
      </div>
      {React.Children.map(children, (child, index) =>
        React.cloneElement(child, { isActive: activeTab === index }),
      )}
    </div>
  );
};
